import React from 'react';
import { TESTIDS } from '@/lib/testIds';
import { Sprig, Gladiolus } from './Illustrations';

export default function About() {
  return (
    <section
      id="about"
      data-testid={TESTIDS.about.section}
      className="relative py-24 md:py-32 bg-[#FBE8EE]/60"
    >
      <Gladiolus className="absolute -right-8 -top-8 w-40 opacity-70 rotate-12 hidden md:block" flip />
      <Sprig className="absolute left-10 bottom-8 w-44 opacity-60" />

      <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-5 gap-14 items-center">
        {/* Portrait card - illustrated */}
        <div className="md:col-span-2">
          <div className="relative">
            <div className="absolute inset-0 -m-4 bg-[#F5C857]/30 blob-shape-2" />
            <div className="absolute -inset-6 bg-[#B88ACB]/20 blob-shape-1 -z-0" />
            <div className="relative rounded-[36px] overflow-hidden bg-white shadow-[0_20px_60px_-15px_rgba(36,56,92,0.25)] p-4">
              <img
                src="/images/erica.jpg"
                alt="Erica Thayer, children's author and former educator"
                className="w-full h-auto rounded-[24px] block object-cover"
                loading="lazy"
              />
              <div className="mt-4 text-center font-accent text-[#8D68B2] text-2xl">Hello!</div>
            </div>
          </div>
        </div>

        {/* Narrative */}
        <div className="md:col-span-3">
          <p className="font-accent text-2xl text-[#8D68B2]">About Erica</p>
          <h2 className="font-heading text-4xl md:text-5xl text-[#24385C] mt-2 leading-tight">
            A teacher&rsquo;s heart. <br/>
            A storyteller&rsquo;s <span className="squiggle-underline">imagination</span>.
          </h2>

          <div className="mt-8 space-y-5 font-body text-[17px] leading-relaxed text-[#24385C]/85">
            <p>
              Erica Thayer is a children&rsquo;s author, former elementary educator, and advocate
              for social-emotional learning. She writes stories that help children understand
              themselves, embrace their emotions, and discover that every feeling has a place.
            </p>
            <p>
              For more than a decade, Erica spent her days alongside young children as they learned
              to read, solve problems, build friendships, and navigate the wonderfully complicated
              work of growing up. She believes every child deserves to feel safe, valued, loved, and
              accepted, and she has seen time and again how the right story can open hearts, spark
              meaningful conversations, and help children make sense of the world around them.
            </p>
            <p>
              When Erica became a mother, those lessons found a new home. Watching her son
              experience the world with curiosity, delight, frustration, and wonder deepened her
              understanding of children&rsquo;s emotional lives. It reminded her that children
              don&rsquo;t need their feelings fixed. They need patient hearts, listening ears, and
              trusted adults willing to sit beside them through every emotion.
            </p>
            <p>
              That realization inspired <em>The Feelings Bench</em>, a story rooted in one simple
              belief: every feeling belongs, and no child should ever have to face their feelings alone.
            </p>
            <p>
              Erica hopes her books become well-loved bedtime stories, treasured classroom
              read-alouds, and conversation starters that children carry with them long after the
              final page. More than anything, she hopes her stories help children feel seen, bring
              families closer together, and remind readers of all ages that every feeling deserves
              compassion, every heart deserves belonging, and no one has to navigate life&rsquo;s
              biggest emotions alone.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { n: '10+', l: 'Years teaching' },
              { n: '1', l: 'Debut picture book' },
              { n: '∞', l: 'Feelings welcomed' },
              { n: '💜', l: 'Stories to come' },
            ].map((s, i) => (
              <div key={i} className="bg-white/80 rounded-2xl p-4 text-center border border-white shadow-sm">
                <div className="font-heading text-2xl text-[#8D68B2] font-semibold">{s.n}</div>
                <div className="font-body text-xs text-[#24385C]/70 mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
